import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import RichTextEditor from "../components/RichTextEditor";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Separator } from "../components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { ScrollArea } from "../components/ui/scroll-area";
import { toast } from "sonner";
import {
  Mail,
  Send,
  Paperclip,
  Users,
  Search,
  CheckCircle,
  XCircle,
  Clock,
  History,
  Trash2,
  Eye,
  X,
  Loader2,
  Building2,
  FileText,
  BarChart3,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MessagingPage() {
  // Compose email state
  const [cabinets, setCabinets] = useState([]);
  const [selectedCabinets, setSelectedCabinets] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  
  // History state
  const [emailHistory, setEmailHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  
  // Stats state
  const [stats, setStats] = useState(null);
  
  // Detail dialog state
  const [detailDialog, setDetailDialog] = useState({ open: false, email: null });
  
  // Active tab
  const [activeTab, setActiveTab] = useState("compose");

  useEffect(() => {
    fetchCabinets();
    fetchStats();
  }, []);

  useEffect(() => {
    if (activeTab === "history") {
      fetchHistory(1);
    }
  }, [activeTab]);

  const fetchCabinets = async () => {
    try {
      const response = await axios.get(`${API_URL}/messaging/cabinets?status=actif`);
      // Exclure le cabinet test 0126BS
      const filteredCabinets = response.data.filter(cab => cab.id_igt !== "0126BS");
      setCabinets(filteredCabinets);
    } catch (error) {
      console.error(/* PROD */ "Error fetching cabinets:", error);
      toast.error("Erreur lors du chargement des cabinets");
    }
  };

  const fetchHistory = async (page = 1) => {
    setHistoryLoading(true);
    try {
      const response = await axios.get(`${API_URL}/messaging/history?page=${page}&limit=10`);
      setEmailHistory(response.data.emails);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error(/* PROD */ "Error fetching history:", error);
      toast.error("Erreur lors du chargement de l'historique");
    } finally {
      setHistoryLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/messaging/stats`);
      setStats(response.data);
    } catch (error) {
      console.error(/* PROD */ "Error fetching stats:", error);
    }
  };

  const handleSelectAll = () => {
    if (selectedCabinets.length === filteredCabinets.length) {
      setSelectedCabinets([]);
    } else {
      setSelectedCabinets(filteredCabinets.map(c => c.id));
    }
  };

  const handleSelectCabinet = (cabinetId) => {
    setSelectedCabinets(prev => 
      prev.includes(cabinetId)
        ? prev.filter(id => id !== cabinetId)
        : [...prev, cabinetId]
    );
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const maxSize = 10 * 1024 * 1024; // 10MB max per file
    
    const validFiles = files.filter(file => {
      if (file.size > maxSize) {
        toast.error(`${file.name} est trop volumineux (max 10MB)`);
        return false;
      }
      return true;
    });
    
    setAttachments(prev => [...prev, ...validFiles]);
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleSendEmail = async () => {
    if (selectedCabinets.length === 0) {
      toast.error("Veuillez sélectionner au moins un destinataire");
      return;
    }
    if (!subject.trim()) {
      toast.error("Le sujet est requis");
      return;
    }
    if (!content.trim()) {
      toast.error("Le contenu est requis");
      return;
    }

    setSending(true);
    try {
      const formData = new FormData();
      formData.append("recipient_ids", JSON.stringify(selectedCabinets));
      formData.append("subject", subject);
      formData.append("content", content);
      
      attachments.forEach(file => {
        formData.append("files", file);
      });

      const response = await axios.post(`${API_URL}/messaging/send`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      const { delivery_stats } = response.data;
      
      if (delivery_stats.failed === 0) {
        toast.success(`Email envoyé avec succès à ${delivery_stats.success} cabinet(s)`);
      } else {
        toast.warning(`Email envoyé à ${delivery_stats.success}/${delivery_stats.total} cabinets. ${delivery_stats.failed} échec(s).`);
      }

      // Reset form
      setSelectedCabinets([]);
      setSubject("");
      setContent("");
      setAttachments([]);
      
      // Refresh stats
      fetchStats();
      
    } catch (error) {
      console.error(/* PROD */ "Error sending email:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setSending(false);
    }
  };

  const viewEmailDetail = async (emailId) => {
    try {
      const response = await axios.get(`${API_URL}/messaging/history/${emailId}`);
      setDetailDialog({ open: true, email: response.data });
    } catch (error) {
      toast.error("Erreur lors du chargement des détails");
    }
  };

  const deleteEmail = async (emailId) => {
    if (!window.confirm("Supprimer cet email de l'historique ?")) return;
    
    try {
      await axios.delete(`${API_URL}/messaging/history/${emailId}`);
      toast.success("Email supprimé");
      fetchHistory(pagination.page);
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la suppression");
    }
  };

  const filteredCabinets = cabinets.filter(cabinet =>
    cabinet.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cabinet.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (cabinet.id_igt && cabinet.id_igt.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const getSelectedCabinetNames = () => {
    return cabinets
      .filter(c => selectedCabinets.includes(c.id))
      .map(c => c.nom)
      .slice(0, 3)
      .join(", ") + (selectedCabinets.length > 3 ? ` +${selectedCabinets.length - 3}` : "");
  };

  return (
    <div className="space-y-6" data-testid="messaging-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Mail className="w-8 h-8 text-[#1B4D3E]" />
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Messagerie</h1>
            <p className="text-muted-foreground">Envoyez des emails aux cabinets habilités</p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Mail className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_emails_sent}</p>
                  <p className="text-xs text-muted-foreground">Emails envoyés</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.success_rate}%</p>
                  <p className="text-xs text-muted-foreground">Taux de livraison</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Users className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_recipients}</p>
                  <p className="text-xs text-muted-foreground">Destinataires total</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Building2 className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.active_cabinets}</p>
                  <p className="text-xs text-muted-foreground">Cabinets actifs</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="compose" className="flex items-center gap-2">
            <Send className="w-4 h-4" />
            Composer
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2">
            <History className="w-4 h-4" />
            Historique
          </TabsTrigger>
        </TabsList>

        {/* Compose Tab */}
        <TabsContent value="compose" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Recipients Selection */}
            <Card className="lg:col-span-1">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Destinataires
                </CardTitle>
                <CardDescription>
                  Sélectionnez les cabinets ({selectedCabinets.length} sélectionné{selectedCabinets.length > 1 ? "s" : ""})
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Rechercher..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9"
                  />
                </div>

                {/* Select All */}
                <div className="flex items-center gap-2 py-2 border-b">
                  <Checkbox
                    checked={selectedCabinets.length === filteredCabinets.length && filteredCabinets.length > 0}
                    onCheckedChange={handleSelectAll}
                  />
                  <span className="text-sm font-medium">Tout sélectionner</span>
                  <Badge variant="secondary" className="ml-auto">{filteredCabinets.length}</Badge>
                </div>

                {/* Cabinet List */}
                <ScrollArea className="h-[300px]">
                  <div className="space-y-2">
                    {filteredCabinets.map(cabinet => (
                      <div
                        key={cabinet.id}
                        className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                          selectedCabinets.includes(cabinet.id)
                            ? "bg-emerald-50 border border-emerald-200"
                            : "hover:bg-gray-50 border border-transparent"
                        }`}
                        onClick={() => handleSelectCabinet(cabinet.id)}
                      >
                        <Checkbox
                          checked={selectedCabinets.includes(cabinet.id)}
                          onCheckedChange={() => handleSelectCabinet(cabinet.id)}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{cabinet.nom}</p>
                          <p className="text-xs text-muted-foreground truncate">{cabinet.email}</p>
                        </div>
                        {cabinet.id_igt && (
                          <Badge variant="outline" className="text-xs">{cabinet.id_igt}</Badge>
                        )}
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Email Composition */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  Rédiger le message
                </CardTitle>
                {selectedCabinets.length > 0 && (
                  <CardDescription>
                    À: {getSelectedCabinetNames()}
                  </CardDescription>
                )}
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Subject */}
                <div className="space-y-2">
                  <Label htmlFor="subject">Sujet *</Label>
                  <Input
                    id="subject"
                    placeholder="Objet de l'email..."
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                  />
                </div>

                {/* Content - Rich Text Editor */}
                <div className="space-y-2">
                  <Label>Message *</Label>
                  <RichTextEditor
                    value={content}
                    onChange={setContent}
                    placeholder="Rédigez votre message ici..."
                  />
                  <p className="text-xs text-muted-foreground">
                    Utilisez la barre d'outils pour formater votre texte (gras, italique, couleur, listes, etc.)
                  </p>
                </div>

                {/* Attachments */}
                <div className="space-y-2">
                  <Label>Pièces jointes</Label>
                  <div className="flex flex-wrap gap-2">
                    {attachments.map((file, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-full text-sm"
                      >
                        <FileText className="w-4 h-4 text-gray-500" />
                        <span className="max-w-[150px] truncate">{file.name}</span>
                        <button
                          onClick={() => removeAttachment(index)}
                          className="text-gray-400 hover:text-red-500"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <label className="flex items-center gap-2 px-3 py-1.5 border border-dashed rounded-full cursor-pointer hover:bg-gray-50 text-sm text-muted-foreground">
                      <Paperclip className="w-4 h-4" />
                      Ajouter un fichier
                      <input
                        type="file"
                        multiple
                        onChange={handleFileChange}
                        className="hidden"
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                      />
                    </label>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Formats acceptés: PDF, Word, Excel, Images (max 10MB par fichier)
                  </p>
                </div>

                <Separator />

                {/* Send Button */}
                <div className="flex justify-end">
                  <Button
                    onClick={handleSendEmail}
                    disabled={sending || selectedCabinets.length === 0 || !subject.trim() || !content.trim()}
                    className="bg-[#1B4D3E] hover:bg-[#163d32]"
                  >
                    {sending ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Envoyer à {selectedCabinets.length} cabinet{selectedCabinets.length > 1 ? "s" : ""}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="w-5 h-5" />
                Historique des emails
              </CardTitle>
              <CardDescription>
                {pagination.total} email{pagination.total > 1 ? "s" : ""} envoyé{pagination.total > 1 ? "s" : ""}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {historyLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-[#1B4D3E]" />
                </div>
              ) : emailHistory.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Mail className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Aucun email envoyé pour le moment</p>
                </div>
              ) : (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Sujet</TableHead>
                        <TableHead>Destinataires</TableHead>
                        <TableHead>Statut</TableHead>
                        <TableHead>Envoyé par</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {emailHistory.map(email => (
                        <TableRow key={email.id}>
                          <TableCell className="text-sm">
                            {new Date(email.sent_at).toLocaleDateString("fr-FR", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit"
                            })}
                          </TableCell>
                          <TableCell>
                            <div className="max-w-[200px]">
                              <p className="font-medium truncate">{email.subject}</p>
                              {email.attachments && email.attachments.length > 0 && (
                                <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                                  <Paperclip className="w-3 h-3" />
                                  {email.attachments.length} fichier{email.attachments.length > 1 ? "s" : ""}
                                </div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <span className="text-sm">{email.delivery_stats?.total || 1}</span>
                              {email.delivery_stats?.failed > 0 && (
                                <Badge variant="destructive" className="text-xs">
                                  {email.delivery_stats.failed} échec
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            {(!email.delivery_stats || email.delivery_stats.failed === 0) ? (
                              <Badge className="bg-emerald-100 text-emerald-700">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                {email.status === "sent" ? "Envoyé" : email.status}
                              </Badge>
                            ) : email.delivery_stats.success > 0 ? (
                              <Badge className="bg-amber-100 text-amber-700">
                                Partiel
                              </Badge>
                            ) : (
                              <Badge variant="destructive">
                                <XCircle className="w-3 h-3 mr-1" />
                                Échec
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-sm">
                            {email.sent_by ? `${email.sent_by.prenom || ''} ${email.sent_by.nom || ''}`.trim() || 'Système' : 'Système'}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => viewEmailDetail(email.id)}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-red-500 hover:text-red-600"
                                onClick={() => deleteEmail(email.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>

                  {/* Pagination */}
                  {pagination.pages > 1 && (
                    <div className="flex items-center justify-between mt-4 pt-4 border-t">
                      <p className="text-sm text-muted-foreground">
                        Page {pagination.page} sur {pagination.pages}
                      </p>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={pagination.page <= 1}
                          onClick={() => fetchHistory(pagination.page - 1)}
                        >
                          <ChevronLeft className="w-4 h-4 mr-1" />
                          Précédent
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={pagination.page >= pagination.pages}
                          onClick={() => fetchHistory(pagination.page + 1)}
                        >
                          Suivant
                          <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Email Detail Dialog */}
      <Dialog open={detailDialog.open} onOpenChange={(open) => setDetailDialog({ open, email: null })}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Détails de l'email</DialogTitle>
            {detailDialog.email && (
              <DialogDescription>
                Envoyé le {new Date(detailDialog.email.sent_at).toLocaleDateString("fr-FR", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit"
                })}
              </DialogDescription>
            )}
          </DialogHeader>
          
          {detailDialog.email && (
            <div className="space-y-4">
              <div>
                <Label className="text-muted-foreground">Sujet</Label>
                <p className="font-medium">{detailDialog.email.subject}</p>
              </div>
              
              <div>
                <Label className="text-muted-foreground">Destinataires ({detailDialog.email.recipients.length})</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {detailDialog.email.recipients.map((r, i) => (
                    <Badge key={i} variant="outline" className="text-xs">
                      {r.cabinet_name}
                    </Badge>
                  ))}
                </div>
              </div>
              
              <Separator />
              
              <div>
                <Label className="text-muted-foreground">Message</Label>
                <div className="mt-2 p-4 bg-gray-50 rounded-lg whitespace-pre-wrap text-sm">
                  {detailDialog.email.content}
                </div>
              </div>
              
              {detailDialog.email.attachments.length > 0 && (
                <div>
                  <Label className="text-muted-foreground">Pièces jointes</Label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {detailDialog.email.attachments.map((att, i) => (
                      <Badge key={i} variant="secondary">
                        <Paperclip className="w-3 h-3 mr-1" />
                        {att}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="grid grid-cols-3 gap-4 pt-4 border-t">
                <div className="text-center">
                  <p className="text-2xl font-bold text-emerald-600">{detailDialog.email.delivery_stats.success}</p>
                  <p className="text-xs text-muted-foreground">Envoyés</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-red-600">{detailDialog.email.delivery_stats.failed}</p>
                  <p className="text-xs text-muted-foreground">Échecs</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold">{detailDialog.email.delivery_stats.total}</p>
                  <p className="text-xs text-muted-foreground">Total</p>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailDialog({ open: false, email: null })}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
